/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useState, useContext, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { RequestMethods, useRequest, useVariables, useLogger } from '../hooks';
import { operationIdState, sessionIdState } from '../states/atoms';
import { imageFace, endPoints, family, userAvatar } from '../states/constants';
import { useAuth } from './AuthProvider';

type IProps = {
  children: React.ReactNode;
};

type ContextProps = {
  facialValidated: boolean;
  onRemoveFacial(): void;
  onSaveFacial(image: string | undefined): void;
  onValidateSpoof(
    encryptedLivenessRaw: Blob | File | null | boolean | undefined,
    operation?: string,
    session?: string
  ): Promise<boolean>;
  face?: string | null;
  operationId?: string;
  userPicture?: string | null;
  onSaveUserPicture(avatar: string | undefined): void;
};

const Ctx = createContext<ContextProps>({
  facialValidated: false,
  onRemoveFacial: () => {},
  onSaveFacial: () => {},
  onValidateSpoof: async () => false,
  face: null,
  userPicture: null,
  onSaveUserPicture: () => {},
});

export const FacialProvider = ({ children }: IProps) => {
  const [userPicture, setUserPicture] = useState<string | null>(
    localStorage.getItem(userAvatar)
  );
  const operationId = useRecoilValue(operationIdState);
  const sessionId = useRecoilValue(sessionIdState);
  const { apiDemoUrl } = useVariables();
  const { demoVersion } = useAuth();

  const [face, setFace] = useState<string | null>(
    localStorage.getItem(imageFace)
  );
  const [facialValidated, setFacialValidated] = useState<boolean>(
    Boolean(localStorage.getItem(imageFace))
  );
  const { captureException } = useLogger();

  useEffect(() => {
    if (face) {
      setFacialValidated(true);
    }
  }, [face]);

  const { request: validateFacialRequest } = useRequest(apiDemoUrl);

  const onSaveFacial = (image: string) => {
    setFace(image);
    localStorage.setItem(imageFace, image);
  };

  const onSaveUserPicture = (avatar: string) => {
    setUserPicture(avatar);
    localStorage.setItem(userAvatar, avatar);
  };

  const onRemoveFacial = () => {
    setFace(null);
    setUserPicture(null);
    setFacialValidated(false);
    localStorage.removeItem(imageFace);
    localStorage.removeItem(userAvatar);
  };

  const onValidateSpoof = async (
    encryptedLivenessRaw: Blob | File
  ): Promise<boolean> => {
    try {
      const resultSpoof = await validateFacialRequest(
        endPoints.Authenticate.Antispoofing,
        {
          method: RequestMethods.post,
          headers: {
            'Content-Type': 'application/octet-stream',
            'x-inphinite-sessionid': sessionId,
            'x-inphinite-operationid': operationId,
            'x-demo-app-version': demoVersion,
            'x-inphinite-family': family,
          },
          data: encryptedLivenessRaw,
        }
      );
      return !resultSpoof?.attack;
    } catch (error: unknown) {
      captureException(error as Error);
      throw new Error(error as string);
    }
  };

  return (
    <Ctx.Provider
      value={{
        facialValidated,
        onSaveFacial,
        onSaveUserPicture,
        onRemoveFacial,
        onValidateSpoof,
        face,
        userPicture,
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useFacialAuth = () => useContext(Ctx);
